import { useState } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { useToast } from '../ui';
import type { Rental } from '../config/supabase';

export interface SignatureData {
  customerSignature?: string | null;
  shopSignature?: string | null;
}

export const usePDFGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { success, error: showError } = useToast();

  const createPDFFromScratch = async (rental: Rental, signatures?: SignatureData): Promise<Uint8Array> => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595, 842]); // A4 size
      const { width, height } = page.getSize();
      
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const customer = rental.customers as any;
      const dress = rental.dresses as any;
      
      const rentalData = {
        customerName: customer?.name || '',
        customerEmail: customer?.email || '',
        customerPhone: customer?.phone || '',
        customerAddress: customer?.address || '',
        location: rental.location || '',
        alterations: rental.alterations || null,
        dressName: dress?.name || '',
        dressBarcode: dress?.barcode || '',
        size: dress?.size || '',
        color: dress?.color || '',
        rentalDate: new Date(rental.rental_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' }),
        returnDate: new Date(rental.expected_return_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' }),
        rentalPrice: rental.rental_price?.toFixed(2) || '0.00',
        depositAmount: (rental.deposit_amount || 0).toFixed(2),
        notes: rental.notes || '',
        contractNumber: `RC-${rental.id.slice(0, 8).toUpperCase()}`
      };

      // Logo/Brand Section (top center)
      try {
        // Load the logo image
        const logoResponse = await fetch('/logo.png');
        if (logoResponse.ok) {
          const logoImageBytes = await logoResponse.arrayBuffer();
          const contentType = logoResponse.headers.get('Content-Type') || '';
          
          let logoImage;
          if (contentType.includes('png')) {
            logoImage = await pdfDoc.embedPng(logoImageBytes);
          } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
            logoImage = await pdfDoc.embedJpg(logoImageBytes);
          } else {
            // Try PNG first, then JPG as fallback
            try {
              logoImage = await pdfDoc.embedPng(logoImageBytes);
            } catch {
              logoImage = await pdfDoc.embedJpg(logoImageBytes);
            }
          }
          
          // Calculate logo dimensions (keeping aspect ratio)
          const logoWidth = 100; // Made it a bit larger
          const logoHeight = logoImage.height * (logoWidth / logoImage.width);
          
          page.drawImage(logoImage, {
            x: width / 2 - logoWidth / 2,
            y: height - 90 - logoHeight,
            width: logoWidth,
            height: logoHeight,
          });
        } else {
          throw new Error('Logo not found');
        }
      } catch (error) {
        console.warn('Could not load logo, using text fallback:', error);
        // Fallback to text if there's any error
        page.drawText('DMU', {
          x: width / 2 - 25,
          y: height - 80,
          size: 24,
          font: boldFont,
          color: rgb(0, 0, 0),
        });

        page.drawText('DRESS ME UP', {
          x: width / 2 - 45,
          y: height - 105,
          size: 14,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      // Top right info
      page.drawText(`Email: ${rentalData.customerEmail}`, {
        x: width - 150,
        y: height - 80,
        size: 9,
        font: font,
        color: rgb(0, 0, 0),
      });

      page.drawText(`Return: ${rentalData.returnDate}`, {
        x: width - 150,
        y: height - 95,
        size: 9,
        font: font,
        color: rgb(0, 0, 0),
      });

      page.drawText(`Contract: ${rentalData.contractNumber}`, {
        x: width - 150,
        y: height - 110,
        size: 9,
        font: font,
        color: rgb(0, 0, 0),
      });

      // Main Title
      page.drawText('DRESS RENTAL SHEET', {
        x: width / 2 - 90,
        y: height - 170, // Moved down to give space for logo
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      let yPos = height - 220; // Adjusted to give more space for logo and title

      // Form Fields - ensure all values are strings
      const formFields = [
        { label: 'NAME OF CLIENT:', value: rentalData.customerName || 'N/A', y: yPos },
        { label: 'LOCATION:', value: rentalData.location || 'N/A', y: yPos - 32 },
        { label: 'PHONE NUMBER:', value: rentalData.customerPhone || 'N/A', y: yPos - 64 },
        { label: 'DATE OF EVENT:', value: rentalData.rentalDate || 'N/A', y: yPos - 96 },
        { label: 'EXPECTED RETURN DATE:', value: rentalData.returnDate || 'N/A', y: yPos - 128 },
        { label: 'DRESS DESCRIPTION:', value: `${rentalData.dressName || 'N/A'} - ${rentalData.color || 'N/A'} - Size ${rentalData.size || 'N/A'}`, y: yPos - 160 },
        { label: 'DRESS RENTAL PRICE:', value: `$${rentalData.rentalPrice}`, y: yPos - 192 },
        { label: 'DEPOSIT & INSURANCE:', value: `$${rentalData.depositAmount}`, y: yPos - 224 }
      ];

      formFields.forEach(field => {
        // Label
        page.drawText(field.label, {
          x: 50,
          y: field.y,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });

        // Value - ensure it's always a string
        page.drawText(String(field.value || ''), {
          x: 250,
          y: field.y,
          size: 11,
          font: font,
          color: rgb(0, 0, 0),
        });

        // Underline for the field
        page.drawLine({
          start: { x: 250, y: field.y - 3 },
          end: { x: 520, y: field.y - 3 },
          thickness: 0.5,
          color: rgb(0, 0, 0),
        });
      });

      // NOTES section
      if (rentalData.notes) {
        const notesY = yPos - 276;
        page.drawText('NOTES:', {
          x: 50,
          y: notesY,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });

        const maxWidth = 470;
        const words = rentalData.notes.split(' ');
        let line = '';
        let lineY = notesY;
        const lineHeight = 14;

        words.forEach((word, index) => {
          const testLine = line + word + ' ';
          const testWidth = font.widthOfTextAtSize(testLine, 10);

          if (testWidth > maxWidth && line !== '') {
            page.drawText(line.trim(), {
              x: 250,
              y: lineY,
              size: 10,
              font: font,
              color: rgb(0, 0, 0),
            });
            line = word + ' ';
            lineY -= lineHeight;
          } else {
            line = testLine;
          }

          if (index === words.length - 1) {
            page.drawText(line.trim(), {
              x: 250,
              y: lineY,
              size: 10,
              font: font,
              color: rgb(0, 0, 0),
            });
          }
        });
      }

      // DRESS ALTERATIONS section
      const alterationsY = yPos - 308;
      page.drawText('DRESS ALTERATIONS', {
        x: 50,
        y: alterationsY,
        size: 11,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      page.drawText('CLIENT', {
        x: 250,
        y: alterationsY,
        size: 10,
        font: font,
        color: rgb(0, 0, 0),
      });

      // CLIENT checkbox
      page.drawRectangle({
        x: 290,
        y: alterationsY - 2,
        width: 12,
        height: 12,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1,
      });

      // Fill CLIENT checkbox if selected
      if (rentalData.alterations === 'CLIENT') {
        page.drawText('X', {
          x: 293,
          y: alterationsY + 1,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      page.drawText('DMU', {
        x: 350,
        y: alterationsY,
        size: 10,
        font: font,
        color: rgb(0, 0, 0),
      });

      // DMU checkbox
      page.drawRectangle({
        x: 380,
        y: alterationsY - 2,
        width: 12,
        height: 12,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1,
      });

      // Fill DMU checkbox if selected
      if (rentalData.alterations === 'DMU') {
        page.drawText('X', {
          x: 383,
          y: alterationsY + 1,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      // SHOP POLICY Section
      const policyY = yPos - 364;
      page.drawText('SHOP POLICY', {
        x: 50,
        y: policyY,
        size: 12,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      const policyItems = [
        '1- In case of cancellation the deposit won\'t be returned',
        '2- In case the dress was damaged, based on our inspection of the damage, we will',
        '   deduct the amount from the insurance to cover the repair fees',
        '3- In case the dress was damaged with no chance of repair, the client must pay the',
        '   rental price multiplied by TWO',
        '4- If the dress cover is not returned an additional 10$ fee is required'
      ];

      let policyItemY = policyY - 25;
      policyItems.forEach(item => {
        page.drawText(item, {
          x: 50,
          y: policyItemY,
          size: 9,
          font: font,
          color: rgb(0, 0, 0),
        });
        policyItemY -= 15;
      });

      const signatureY = policyItemY - 40;

      page.drawText('Customer Signature:', {
        x: 50,
        y: signatureY,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      page.drawLine({
        start: { x: 50, y: signatureY - 50 },
        end: { x: 250, y: signatureY - 50 },
        thickness: 0.5,
        color: rgb(0, 0, 0),
      });

      page.drawText('Shop Representative:', {
        x: 320,
        y: signatureY,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      page.drawLine({
        start: { x: 320, y: signatureY - 50 },
        end: { x: 520, y: signatureY - 50 },
        thickness: 0.5,
        color: rgb(0, 0, 0),
      });

      if (signatures?.customerSignature) {
        try {
          const signatureBytes = await fetch(signatures.customerSignature).then(res => res.arrayBuffer());
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          const sigWidth = 150;
          const sigHeight = signatureImage.height * (sigWidth / signatureImage.width);
          page.drawImage(signatureImage, {
            x: 50,
            y: signatureY - 48,
            width: sigWidth,
            height: Math.min(sigHeight, 40),
          });
        } catch (e) {
          console.warn('Could not embed customer signature:', e);
        }
      }

      if (signatures?.shopSignature) {
        try {
          const signatureBytes = await fetch(signatures.shopSignature).then(res => res.arrayBuffer());
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          const sigWidth = 150;
          const sigHeight = signatureImage.height * (sigWidth / signatureImage.width);
          page.drawImage(signatureImage, {
            x: 320,
            y: signatureY - 48,
            width: sigWidth,
            height: Math.min(sigHeight, 40),
          });
        } catch (e) {
          console.warn('Could not embed shop signature:', e);
        }
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('Error creating PDF:', error);
      throw new Error('Failed to generate PDF contract');
    }
  };

  const fillExistingPDF = async (rental: Rental, signatures?: SignatureData): Promise<Uint8Array> => {
    try {
      const pdfPath = encodeURI('/RENTAL SHEET EVENING AND WEDDING FINAL OUTLINED LATEST.pdf');
      const response = await fetch(pdfPath);

      if (!response.ok) {
        console.warn('Could not load PDF template, creating from scratch');
        return await createPDFFromScratch(rental, signatures);
      }
      
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { height } = firstPage.getSize();
      
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const customer = rental.customers as any;
      const dress = rental.dresses as any;
      
      const rentalData = {
        customerName: customer?.name || 'N/A',
        customerEmail: customer?.email || 'N/A',
        customerPhone: customer?.phone || 'N/A',
        customerAddress: customer?.address || 'N/A',
        location: rental.location || 'N/A',
        alterations: rental.alterations || null,
        dressName: dress?.name || 'N/A',
        dressBarcode: dress?.barcode || 'N/A',
        size: dress?.size || 'N/A',
        color: dress?.color || 'N/A',
        rentalDate: new Date(rental.rental_date).toLocaleDateString(),
        returnDate: new Date(rental.expected_return_date).toLocaleDateString(),
        rentalPrice: `$${rental.rental_price?.toFixed(2) || '0.00'}`,
        notes: rental.notes || 'None',
        contractDate: new Date().toLocaleDateString(),
        contractNumber: `RC-${rental.id.slice(0, 8).toUpperCase()}`
      };

      // Add overlay text positioned to match your PDF form fields
      // Based on your PDF form layout - adjusting coordinates to align with actual fields
      const overlayElements = [
        // NAME OF CLIENT field - appears to be around line 1 on your form
        { text: rentalData.customerName || 'N/A', x: 280, y: height - 303, size: 12, font: boldFont },

        // LOCATION field - second line
        { text: rentalData.location || 'N/A', x: 280, y: height - 335, size: 11, font: font },

        // PHONE NUMBER field - third line
        { text: rentalData.customerPhone || 'N/A', x: 280, y: height - 367, size: 11, font: font },

        // DATE OF EVENT field - fourth line
        { text: rentalData.rentalDate || 'N/A', x: 280, y: height - 399, size: 11, font: font },

        // EXPECTED RETURN DATE field - fifth line
        { text: rentalData.returnDate || 'N/A', x: 280, y: height - 431, size: 11, font: font },

        // DRESS DESCRIPTION field - sixth line
        { text: `${rentalData.dressName || 'N/A'} - ${rentalData.color || 'N/A'} - Size ${rentalData.size || 'N/A'}`, x: 280, y: height - 463, size: 10, font: font },

        // DRESS RENTAL PRICE field - seventh line
        { text: `$${rental.rental_price?.toFixed(2) || '0.00'}`, x: 280, y: height - 495, size: 12, font: boldFont },

        // DEPOSIT & INSURANCE field - eighth line
        { text: `$${(rental.deposit_amount || 0).toFixed(2)}`, x: 280, y: height - 527, size: 11, font: font },

        // Additional info in top right corner (smaller and positioned better)
        { text: `Email: ${rentalData.customerEmail || 'N/A'}`, x: 535, y: height - 153, size: 9, font: font },
        { text: `Return: ${rentalData.returnDate || 'N/A'}`, x: 535, y: height - 173, size: 9, font: font },
        { text: `Contract: ${rentalData.contractNumber || 'N/A'}`, x: 535, y: height - 193, size: 9, font: font },
      ];

      overlayElements.forEach(({ text, x, y, size, font: textFont }) => {
        firstPage.drawText(String(text || ''), {
          x,
          y,
          size,
          font: textFont,
          color: rgb(0, 0, 0),
        });
      });

      // Add notes if present
      if (rentalData.notes && rentalData.notes !== 'None') {
        const notesY = height - 559;
        firstPage.drawText('NOTES:', {
          x: 50,
          y: notesY,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });

        const maxWidth = 470;
        const words = rentalData.notes.split(' ');
        let line = '';
        let lineY = notesY;
        const lineHeight = 14;

        words.forEach((word, index) => {
          const testLine = line + word + ' ';
          const testWidth = font.widthOfTextAtSize(testLine, 10);

          if (testWidth > maxWidth && line !== '') {
            firstPage.drawText(line.trim(), {
              x: 280,
              y: lineY,
              size: 10,
              font: font,
              color: rgb(0, 0, 0),
            });
            line = word + ' ';
            lineY -= lineHeight;
          } else {
            line = testLine;
          }

          if (index === words.length - 1) {
            firstPage.drawText(line.trim(), {
              x: 280,
              y: lineY,
              size: 10,
              font: font,
              color: rgb(0, 0, 0),
            });
          }
        });
      }

      // Add alterations checkboxes if selected
      // These coordinates should match your existing PDF template's checkbox positions
      if (rentalData.alterations === 'CLIENT') {
        firstPage.drawText('X', {
          x: 293, // Adjust this x coordinate to match your PDF template
          y: height - 590, // Adjust this y coordinate to match your PDF template
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      if (rentalData.alterations === 'DMU') {
        firstPage.drawText('X', {
          x: 383,
          y: height - 590,
          size: 11,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
      }

      const signatureY = height - 720;

      firstPage.drawText('Customer Signature:', {
        x: 50,
        y: signatureY,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawLine({
        start: { x: 50, y: signatureY - 50 },
        end: { x: 250, y: signatureY - 50 },
        thickness: 0.5,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText('Shop Representative:', {
        x: 320,
        y: signatureY,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawLine({
        start: { x: 320, y: signatureY - 50 },
        end: { x: 520, y: signatureY - 50 },
        thickness: 0.5,
        color: rgb(0, 0, 0),
      });

      if (signatures?.customerSignature) {
        try {
          const signatureBytes = await fetch(signatures.customerSignature).then(res => res.arrayBuffer());
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          const sigWidth = 150;
          const sigHeight = signatureImage.height * (sigWidth / signatureImage.width);
          firstPage.drawImage(signatureImage, {
            x: 50,
            y: signatureY - 48,
            width: sigWidth,
            height: Math.min(sigHeight, 40),
          });
        } catch (e) {
          console.warn('Could not embed customer signature:', e);
        }
      }

      if (signatures?.shopSignature) {
        try {
          const signatureBytes = await fetch(signatures.shopSignature).then(res => res.arrayBuffer());
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          const sigWidth = 150;
          const sigHeight = signatureImage.height * (sigWidth / signatureImage.width);
          firstPage.drawImage(signatureImage, {
            x: 320,
            y: signatureY - 48,
            width: sigWidth,
            height: Math.min(sigHeight, 40),
          });
        } catch (e) {
          console.warn('Could not embed shop signature:', e);
        }
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('Error filling existing PDF:', error);
      return await createPDFFromScratch(rental, signatures);
    }
  };

  const generatePDF = async (rental: Rental, useExisting: boolean = true, signatures?: SignatureData) => {
    setLoading(true);
    try {
      const pdfBytes = useExisting
        ? await fillExistingPDF(rental, signatures)
        : await createPDFFromScratch(rental, signatures);
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Clean up previous URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      setPdfUrl(url);
      success('PDF contract generated successfully!');
      return url;
    } catch (error: any) {
      showError(error.message || 'Failed to generate PDF');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (rental: Rental, url?: string) => {
    const customer = rental.customers as any;
    const filename = `rental-contract-${customer?.name?.replace(/\s+/g, '-') || 'customer'}-${rental.id.slice(0, 8)}.pdf`;
    
    const link = document.createElement('a');
    link.href = url || pdfUrl || '';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    success('PDF downloaded successfully!');
  };

  const formatPhoneNumber = (phone?: string): string => {
    if (!phone || phone.trim() === '') return '';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Must have at least 10 digits to be valid
    if (digits.length < 10) return '';
    // Add country code if not present (assuming US/CA)
    if (digits.length === 10) {
      return `1${digits}`;
    }
    return digits;
  };

  const isValidPhoneNumber = (phone?: string): boolean => {
    if (!phone || phone.trim() === '') return false;
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const createWhatsAppMessage = (rental: Rental): string => {
    const customer = rental.customers as any;
    const dress = rental.dresses as any;
    const depositAmount = (rental.deposit_amount || 0).toFixed(2);
    
    return `Hi ${customer?.name || 'Customer'}! 👋

Your dress rental contract is ready! 📋✨

*RENTAL DETAILS:*
👗 *Dress:* ${dress?.name || 'N/A'}
📏 *Size:* ${dress?.size || 'N/A'} | *Color:* ${dress?.color || 'N/A'}
📅 *Event Date:* ${new Date(rental.rental_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' })}
📦 *Return Date:* ${new Date(rental.expected_return_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' })}

*PRICING:*
💰 *Rental Price:* $${rental.rental_price?.toFixed(2) || '0.00'}
💳 *Deposit Required:* $${depositAmount}
🛡️ *Insurance:* Included

*NEXT STEPS:*
1️⃣ Review the attached contract
2️⃣ Sign and return if everything looks correct
3️⃣ Schedule pickup/fitting appointment
4️⃣ Bring deposit on pickup day

*IMPORTANT REMINDERS:*
⚠️ Deposit is non-refundable if cancelled
⚠️ Any damage will be assessed per contract terms
⚠️ Late returns may incur additional fees

Questions? Reply to this message or call us! 

Thank you for choosing our dress rental service! 💕

_Contract #${rental.id.slice(0, 8).toUpperCase()}_`;
  };

  const sendToWhatsApp = async (rental: Rental, autoPDF: boolean = true) => {
    const customer = rental.customers as any;
    
    // Check if customer has a valid phone number
    if (!isValidPhoneNumber(customer?.phone)) {
      showError('Customer phone number is missing or invalid. Please add a valid phone number to the customer profile first.');
      return;
    }
    
    // Auto-download PDF first for easy attachment
    if (autoPDF && pdfUrl) {
      downloadPDF(rental);
      // Small delay to ensure download starts
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    const message = createWhatsAppMessage(rental);
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = formatPhoneNumber(customer?.phone);
    
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    success(`PDF downloaded! WhatsApp opened for ${customer?.name}. Please attach the downloaded PDF to your message.`);
  };

  const cleanup = () => {
    if (pdfUrl) {
      console.log('Cleaning up PDF URL:', pdfUrl);
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return {
    loading,
    pdfUrl,
    generatePDF,
    downloadPDF,
    sendToWhatsApp,
    cleanup
  };
}; 